// src/components/common/GameCard.jsx — Epic Games Store style
import { useState } from "react";
import { Link } from "react-router-dom";

const ACC_CFG = {
  steam:        { label:"Steam",        icon:"🎮", color:"#1b9aff", bg:"rgba(27,154,255,0.12)",  border:"rgba(27,154,255,0.25)" },
  epic:         { label:"Epic",         icon:"⬛", color:"#e0e0e0", bg:"rgba(224,224,224,0.08)", border:"rgba(224,224,224,0.18)" },
  game_account: { label:"Account",      icon:"👤", color:"#a78bfa", bg:"rgba(167,139,250,0.12)", border:"rgba(167,139,250,0.25)" },
  shared:       { label:"Shared",       icon:"🔗", color:"#34d399", bg:"rgba(52,211,153,0.1)",   border:"rgba(52,211,153,0.22)" },
  private:      { label:"Private",      icon:"🔒", color:"#fbbf24", bg:"rgba(251,191,36,0.1)",   border:"rgba(251,191,36,0.22)" },
  family:       { label:"Family Share", icon:"👨‍👩‍👧", color:"#f97316", bg:"rgba(249,115,22,0.1)",  border:"rgba(249,115,22,0.22)" },
};

function isNewGame(createdAt) {
  if (!createdAt) return false;
  const ts = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
  return (Date.now() - ts.getTime()) < 7 * 24 * 60 * 60 * 1000;
}

export default function GameCard({ game, index = 0 }) {
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(game.likes || 0);
  const isOOS = game.stock <= 0;
  const isNew = isNewGame(game.createdAt);
  const acc = ACC_CFG[game.accountType] || ACC_CFG.steam;

  function onLike(e) {
    e.preventDefault(); e.stopPropagation();
    setLiked(p => { setLikes(c => c + (p ? -1 : 1)); return !p; });
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .gc {
          display: block; text-decoration: none;
          background: #1a1a1a; border-radius: 8px;
          overflow: hidden; position: relative;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          animation: gcIn 0.4s ease both;
          border: 1px solid rgba(255,255,255,0.06);
        }
        .gc:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.5);
          border-color: rgba(255,255,255,0.12);
        }
        .gc.oos { opacity: 0.5; }
        .gc.oos:hover { transform: none; box-shadow: none; }

        @keyframes gcIn {
          from { opacity:0; transform:translateY(16px); }
          to   { opacity:1; transform:translateY(0); }
        }

        /* Image */
        .gc-img-wrap {
          aspect-ratio: 3/4;
          overflow: hidden; position: relative;
          background: #111;
        }
        .gc-img { width:100%; height:100%; object-fit:cover; transition: transform 0.4s ease; display:block; }
        .gc:hover .gc-img { transform: scale(1.04); }
        .gc-img-placeholder {
          width:100%; height:100%; display:flex; align-items:center; justify-content:center;
          font-size:56px; background:linear-gradient(135deg,#1a1a1a,#2a2a2a);
        }

        /* Badges on image */
        .gc-badge-row {
          position:absolute; top:10px; left:10px;
          display:flex; flex-direction:column; gap:5px; align-items:flex-start;
        }
        .gc-badge-featured {
          background: #0078F2; color:#fff;
          font-family:'Inter',sans-serif; font-size:11px; font-weight:700;
          padding:4px 10px; border-radius:4px; letter-spacing:0.5px;
          text-transform:uppercase;
        }
        .gc-badge-new {
          background: linear-gradient(90deg,#22c55e,#16a34a); color:#fff;
          font-family:'Inter',sans-serif; font-size:11px; font-weight:700;
          padding:4px 10px; border-radius:4px; letter-spacing:0.5px;
          text-transform:uppercase;
          animation: newPulse 2s ease infinite;
        }
        @keyframes newPulse {
          0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,0.4)}
          50%{box-shadow:0 0 0 6px rgba(34,197,94,0)}
        }
        .gc-badge-stock {
          position:absolute; top:10px; right:10px;
          font-family:'Inter',sans-serif; font-size:11px; font-weight:600;
          padding:4px 10px; border-radius:4px;
        }
        .gc-badge-stock.in   { background:rgba(34,197,94,0.2);  color:#22c55e; border:1px solid rgba(34,197,94,0.3); }
        .gc-badge-stock.low  { background:rgba(234,179,8,0.2);  color:#eab308; border:1px solid rgba(234,179,8,0.3); }
        .gc-badge-stock.out  { background:rgba(239,68,68,0.15); color:#ef4444; border:1px solid rgba(239,68,68,0.25); }

        /* OOS full overlay */
        .gc-oos-overlay {
          position:absolute; inset:0;
          background:rgba(0,0,0,0.55);
          display:flex; align-items:center; justify-content:center;
        }
        .gc-oos-label {
          font-family:'Inter',sans-serif; font-size:13px; font-weight:800;
          color:#fff; background:rgba(239,68,68,0.75); border:1px solid rgba(239,68,68,0.5);
          padding:8px 20px; border-radius:6px; letter-spacing:0.5px; text-transform:uppercase;
        }

        /* Like on image */
        .gc-like {
          position:absolute; bottom:10px; right:10px;
          background:rgba(0,0,0,0.6); backdrop-filter:blur(8px);
          border:1px solid rgba(255,255,255,0.12); border-radius:20px;
          padding:5px 10px; display:flex; align-items:center; gap:5px;
          font-family:'Inter',sans-serif; font-size:12px; font-weight:500; color:#a3a3a3;
          cursor:pointer; transition:all 0.15s; z-index:2;
        }
        .gc-like:hover { border-color:rgba(239,68,68,0.4); color:#f87171; }
        .gc-like.liked { background:rgba(239,68,68,0.15); border-color:rgba(239,68,68,0.35); color:#f87171; }

        /* Hover buy overlay */
        .gc-overlay {
          position:absolute; inset:0;
          background:linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 50%);
          opacity:0; transition:opacity 0.2s;
          display:flex; align-items:flex-end; padding:16px;
        }
        .gc:hover .gc-overlay { opacity:1; }
        .gc-overlay-btn {
          width:100%; padding:11px; background:#0078F2; border:none; border-radius:6px;
          font-family:'Inter',sans-serif; font-size:14px; font-weight:700; color:#fff;
          cursor:pointer; transition:background 0.15s;
          transform:translateY(6px); transition:all 0.2s;
        }
        .gc:hover .gc-overlay-btn { transform:translateY(0); }
        .gc-overlay-btn:hover { background:#0a84ff; }

        /* Body */
        .gc-body { padding:14px 14px 16px; }
        .gc-acc-row { display:flex; align-items:center; gap:6px; margin-bottom:8px; }
        .gc-acc-pill {
          display:inline-flex; align-items:center; gap:4px;
          font-family:'Inter',sans-serif; font-size:11px; font-weight:600;
          padding:3px 8px; border-radius:4px; letter-spacing:0;
        }
        .gc-genres { display:flex; gap:5px; flex-wrap:wrap; margin-bottom:8px; }
        .gc-genre {
          font-family:'Inter',sans-serif; font-size:11px; font-weight:500; color:#6b6b6b;
          background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
          border-radius:4px; padding:2px 7px;
        }
        .gc-title {
          font-family:'Inter',sans-serif; font-size:16px; font-weight:700; color:#fff;
          line-height:1.3; margin-bottom:12px;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
        }
        .gc-footer {
          display:flex; align-items:center; justify-content:space-between;
          padding-top:10px; border-top:1px solid rgba(255,255,255,0.06);
        }
        .gc-price {
          font-family:'Inter',sans-serif; font-size:22px; font-weight:800; color:#0078F2;
        }
        .gc-price-sub {
          font-family:'Inter',sans-serif; font-size:11px; color:#6b6b6b; margin-top:1px;
        }
        .gc-rating {
          font-family:'Inter',sans-serif; font-size:13px; font-weight:600; color:#eab308;
          display:flex; align-items:center; gap:4px;
        }

        @media(max-width:480px) {
          .gc-title { font-size:14px; }
          .gc-price { font-size:18px; }
        }
      `}</style>

      <Link
        to={`/game/${game.id}`}
        className={`gc ${isOOS ? "oos" : ""}`}
        style={{ animationDelay:`${index * 0.05}s` }}
      >
        <div className="gc-img-wrap">
          {game.coverImage
            ? <img src={game.coverImage} alt={game.title} className="gc-img" />
            : <div className="gc-img-placeholder">🎮</div>
          }

          {/* Top-left badge stack */}
          <div className="gc-badge-row">
            {game.featured && <div className="gc-badge-featured">Featured</div>}
            {isNew && !isOOS && <div className="gc-badge-new">New</div>}
          </div>

          {/* Top-right stock badge */}
          <div className={`gc-badge-stock ${isOOS ? "out" : game.stock <= 2 ? "low" : "in"}`}>
            {isOOS ? "Sold Out" : game.stock <= 2 ? `${game.stock} left` : "In Stock"}
          </div>

          {/* Like button */}
          <button className={`gc-like ${liked ? "liked" : ""}`} onClick={onLike}>
            {liked ? "❤️" : "🤍"} {likes}
          </button>

          {/* Hover overlay */}
          {isOOS ? (
            <div className="gc-oos-overlay">
              <div className="gc-oos-label">Sold Out</div>
            </div>
          ) : (
            <div className="gc-overlay">
              <button className="gc-overlay-btn">View Game</button>
            </div>
          )}
        </div>

        <div className="gc-body">
          <div className="gc-acc-row">
            <span className="gc-acc-pill" style={{ background:acc.bg, border:`1px solid ${acc.border}`, color:acc.color }}>
              {acc.icon} {acc.label}
            </span>
          </div>
          <div className="gc-genres">
            {(game.genre || []).slice(0,2).map(g => <span key={g} className="gc-genre">{g}</span>)}
          </div>
          <div className="gc-title">{game.title}</div>
          <div className="gc-footer">
            <div>
              <div className="gc-price">${game.price?.toFixed(2)}</div>
              <div className="gc-price-sub">USD · Offline Access</div>
            </div>
            {game.rating && <div className="gc-rating">★ {game.rating}</div>}
          </div>
        </div>
      </Link>
    </>
  );
}